/**
 * Recent Patients Storage Service
 *
 * Provides staffId-based isolation for recent patients using IndexedDB
 * to prevent cross-user data leakage while maintaining persistence
 * across browser sessions.
 */

const DB_NAME = "MPSRecentPatients";
const DB_VERSION = 1;
const STORE_NAME = "recentPatients";
const MAX_RECENT_PATIENTS = 100;

class RecentPatientsStorageService {
  constructor() {
    this.db = null;
    this.dbPromise = this.initDB();
  }

  /**
   * Initialize IndexedDB database
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("Failed to open IndexedDB for recent patients");
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object store with staffId as key
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: "staffId",
          });
          store.createIndex("staffId", "staffId", { unique: true });
        }
      };
    });
  }

  /**
   * Get current staff ID from Redux store
   */
  getCurrentStaffId() {
    try {
      // Import store dynamically to avoid circular dependencies
      const { store } = require("../State/store/configureStore");
      const state = store.getState();
      const staffId =
        state?.user?.data?.id ||
        state?.user?.data?.staffId ||
        state?.user?.data?.userId ||
        null;

      if (!staffId) {
        console.warn("No staff ID available in Redux state:", {
          userDataKeys: state?.user?.data
            ? Object.keys(state.user.data)
            : "no user data",
          loggedIn: state?.user?.loggedIn,
        });
      }

      return staffId;
    } catch (error) {
      console.warn("Could not get current staff ID:", error);
      return null;
    }
  }

  /**
   * Add patient to recent list for current staff
   */
  async addToRecent(patientId) {
    try {
      const staffId = this.getCurrentStaffId();
      if (!staffId) {
        console.warn("No staff ID available, cannot add to recent patients");
        return;
      }

      //console.log(`Adding patient ${patientId} to recent list for staff ${staffId}`);
      await this.dbPromise;

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      // Get existing recent patients for this staff
      const getRequest = store.get(staffId);

      getRequest.onsuccess = () => {
        const existingData = getRequest.result;
        let recentPatients = existingData ? existingData.patientIds || [] : [];

        //console.log(
        //   `Existing recent patients for staff ${staffId}:`,
        //   recentPatients
        // );

        // Remove patient if already in list
        recentPatients = recentPatients.filter((id) => id !== patientId);

        // Add to front of list
        recentPatients.unshift(patientId);

        // Keep only MAX_RECENT_PATIENTS
        recentPatients = recentPatients.slice(0, MAX_RECENT_PATIENTS);

        //console.log(
        //  `Updated recent patients for staff ${staffId}:`,
        //  recentPatients
        //);

        // Store updated list
        const data = {
          staffId: staffId,
          patientIds: recentPatients,
          lastUpdated: new Date().toISOString(),
        };

        const putRequest = store.put(data);
        putRequest.onsuccess = () => {
          console.log(
            `Successfully stored recent patients for staff ${staffId}`
          );
        };
        putRequest.onerror = () => {
          console.error(`Failed to store recent patients for staff ${staffId}`);
        };
      };

      getRequest.onerror = () => {
        console.error("Failed to get recent patients for staff:", staffId);
      };
    } catch (error) {
      console.error("Error adding patient to recent list:", error);
      // Fallback to localStorage for backwards compatibility
      this.fallbackToLocalStorage(patientId);
    }
  }

  /**
   * Get recent patient IDs for current staff
   */
  async getRecentPatientIds() {
    try {
      const staffId = this.getCurrentStaffId();
      if (!staffId) {
        console.warn("No staff ID available, returning empty recent patients");
        return [];
      }

      await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(staffId);

        request.onsuccess = () => {
          const result = request.result;
          const patientIds = result ? result.patientIds || [] : [];
          //console.log(
          //  `Retrieved ${patientIds.length} recent patients for staff ${staffId}:`,
          //  patientIds
          // );
          resolve(patientIds);
        };

        request.onerror = () => {
          console.error("Failed to get recent patients for staff:", staffId);
          // Fallback to localStorage for backwards compatibility
          resolve(this.fallbackGetFromLocalStorage());
        };
      });
    } catch (error) {
      console.error("Error getting recent patient IDs:", error);
      // Fallback to localStorage for backwards compatibility
      return this.fallbackGetFromLocalStorage();
    }
  }

  /**
   * Clear recent patients for current staff (useful on logout)
   */
  async clearRecentForCurrentStaff() {
    try {
      const staffId = this.getCurrentStaffId();
      if (!staffId) {
        return;
      }

      await this.dbPromise;

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      store.delete(staffId);
    } catch (error) {
      console.error("Error clearing recent patients:", error);
    }
  }

  /**
   * Clear all recent patients data (useful for admin cleanup)
   */
  async clearAllRecentPatients() {
    try {
      await this.dbPromise;

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
    } catch (error) {
      console.error("Error clearing all recent patients:", error);
    }
  }

  /**
   * Fallback to localStorage for backwards compatibility
   */
  fallbackToLocalStorage(patientId) {
    try {
      const StorageService = require("./storageService").default;
      const RECENT_PATIENTS = "RECENT_PATIENTS";

      let recents = StorageService.getFromLocalStorage(RECENT_PATIENTS) || [];
      let newRecents = [];

      for (let i = 0; i < recents.length; i++) {
        if (recents[i] !== patientId) {
          newRecents.push(recents[i]);
        }
      }
      newRecents.unshift(patientId);
      StorageService.addToLocalStorage(
        RECENT_PATIENTS,
        newRecents.slice(0, MAX_RECENT_PATIENTS)
      );
    } catch (error) {
      console.error("Fallback to localStorage failed:", error);
    }
  }

  /**
   * Fallback to get from localStorage for backwards compatibility
   */
  fallbackGetFromLocalStorage() {
    try {
      const StorageService = require("./storageService").default;
      const RECENT_PATIENTS = "RECENT_PATIENTS";
      return StorageService.getFromLocalStorage(RECENT_PATIENTS) || [];
    } catch (error) {
      console.error("Fallback get from localStorage failed:", error);
      return [];
    }
  }

  /**
   * Debug helper: List all recent patients data in IndexedDB
   */
  async debugListAllRecentPatients() {
    try {
      await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const results = request.result;
          console.log("All recent patients data in IndexedDB:", results);
          resolve(results);
        };

        request.onerror = () => {
          console.error("Failed to get all recent patients data");
          reject(request.error);
        };
      });
    } catch (error) {
      console.error("Error listing all recent patients:", error);
      return [];
    }
  }

  /**
   * Migrate existing localStorage recent patients to IndexedDB for current staff
   */
  async migrateFromLocalStorage() {
    try {
      const staffId = this.getCurrentStaffId();
      if (!staffId) {
        return;
      }

      const StorageService = require("./storageService").default;
      const RECENT_PATIENTS = "RECENT_PATIENTS";
      const existingRecents =
        StorageService.getFromLocalStorage(RECENT_PATIENTS) || [];

      if (existingRecents.length > 0) {
        // Check if we already have data for this staff in IndexedDB
        const existingIndexedDBData = await this.getRecentPatientIds();

        if (existingIndexedDBData.length === 0) {
          // Migrate the localStorage data to IndexedDB for this staff
          await this.dbPromise;

          const transaction = this.db.transaction([STORE_NAME], "readwrite");
          const store = transaction.objectStore(STORE_NAME);

          const data = {
            staffId: staffId,
            patientIds: existingRecents.slice(0, MAX_RECENT_PATIENTS),
            lastUpdated: new Date().toISOString(),
            migratedFromLocalStorage: true,
          };

          store.put(data);

          // Clear localStorage to prevent future confusion
          StorageService.removeFromLocalStorage(RECENT_PATIENTS);

          console.log(
            `Migrated ${existingRecents.length} recent patients from localStorage to IndexedDB for staff ${staffId}`
          );
        }
      }
    } catch (error) {
      console.error("Error migrating from localStorage:", error);
    }
  }
}

const recentPatientsStorageService = new RecentPatientsStorageService();

// Add debug helper to window for testing
if (typeof window !== "undefined") {
  window.debugRecentPatients = () =>
    recentPatientsStorageService.debugListAllRecentPatients();
}

export default recentPatientsStorageService;
